// Settings → Password. Change the signed-in person's own password.
//
// Endpoint: POST /api/portal/password { currentPassword, newPassword,
// confirmPassword } (kitchenos-api modules/retailer_credentials.js).
// On success the server ends every OTHER session for this person and sends
// this device a fresh cookie, so the user stays signed in here.
//
// Open to every role — owners and invited team members alike — so it is NOT
// in the settings TAB_PERM map and index.ts never greys it out.

import { ApiError } from "@tkos/api-client";
import type { SettingsTab, TabCleanup } from "../types";
import type { PageContext } from "../../../shell/types";

export const MIN_PASSWORD_LENGTH = 12;

/** Same rule the server applies; null when acceptable. */
export function newPasswordProblem(pw: string): string | null {
  if (pw.length < MIN_PASSWORD_LENGTH) return `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  if (pw.length > 200) return "Use no more than 200 characters.";
  if (pw.trim() !== pw) return "Your password can’t start or end with a space.";
  return null;
}

function serverMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return "Too many attempts. Please wait 15 minutes and try again.";
    try {
      const body = JSON.parse(err.message) as { error?: unknown };
      if (typeof body.error === "string" && body.error) return body.error;
    } catch { /* not JSON */ }
  }
  return "Your password couldn’t be changed. Please try again.";
}

const password: SettingsTab = {
  id: "password",
  label: "Password",
  mount(container: HTMLElement, ctx: PageContext): TabCleanup {
    container.innerHTML = `
      <section class="set-section set-password">
        <h3 class="set-section-title">Change password</h3>
        <p class="set-password-lede">Changing your password signs you out on every other device. You stay signed in here.</p>
        <form class="set-password-form" novalidate>
          <div class="set-field">
            <label for="set-pw-current">Current password</label>
            <input class="set-input" type="password" id="set-pw-current" name="currentPassword" autocomplete="current-password" required>
          </div>
          <div class="set-field">
            <label for="set-pw-new">New password</label>
            <input class="set-input" type="password" id="set-pw-new" name="newPassword" autocomplete="new-password"
                   minlength="${MIN_PASSWORD_LENGTH}" aria-describedby="set-pw-rule" required>
            <p class="set-password-rule" id="set-pw-rule">At least ${MIN_PASSWORD_LENGTH} characters.</p>
          </div>
          <div class="set-field">
            <label for="set-pw-confirm">Confirm new password</label>
            <input class="set-input" type="password" id="set-pw-confirm" name="confirmPassword" autocomplete="new-password"
                   minlength="${MIN_PASSWORD_LENGTH}" required>
          </div>
          <p class="set-password-error" data-role="error" role="alert" aria-live="polite"></p>
          <button type="submit" class="set-btn" data-role="submit">Change password</button>
        </form>
      </section>
    `;
    const form = container.querySelector<HTMLFormElement>(".set-password-form")!;
    const current = form.querySelector<HTMLInputElement>("#set-pw-current")!;
    const next = form.querySelector<HTMLInputElement>("#set-pw-new")!;
    const confirm = form.querySelector<HTMLInputElement>("#set-pw-confirm")!;
    const errEl = form.querySelector<HTMLParagraphElement>('[data-role="error"]')!;
    const btn = form.querySelector<HTMLButtonElement>('[data-role="submit"]')!;
    let alive = true;

    const onSubmit = (e: Event): void => {
      e.preventDefault();
      errEl.textContent = "";
      if (!current.value) { errEl.textContent = "Enter your current password."; current.focus(); return; }
      const problem = newPasswordProblem(next.value);
      if (problem) { errEl.textContent = problem; next.focus(); return; }
      if (next.value !== confirm.value) { errEl.textContent = "The new passwords don’t match."; confirm.focus(); return; }
      btn.disabled = true;
      btn.textContent = "Changing…";
      void (async () => {
        try {
          await ctx.api<{ ok: boolean }>("/portal/password", {
            method: "POST",
            body: JSON.stringify({
              currentPassword: current.value,
              newPassword: next.value,
              confirmPassword: confirm.value,
            }),
          });
          if (!alive) return;
          form.reset();
          ctx.showToast("Password changed. You’ve been signed out on your other devices.", "success");
        } catch (err) {
          if (!alive) return;
          errEl.textContent = serverMessage(err);
        } finally {
          if (alive) { btn.disabled = false; btn.textContent = "Change password"; }
        }
      })();
    };
    form.addEventListener("submit", onSubmit);

    return () => {
      alive = false;
      form.removeEventListener("submit", onSubmit);
    };
  },
};

export default password;
